import { useContext, useEffect, useRef } from 'react';
import { ClientConfigurationContext } from '../context/ClientConfigurationContextProvider';
import { GeaoContext } from '../components/GeaoOnboarding/GeaoContextProvider';
import { buildGtmEventMetadata, GtmEventType, resolvePageNames, trackGtmEvent } from '../util/gtmTracking';
import { useGtmPageViewId } from '../context/GtmPageViewContext';

export { GtmEventType } from '../util/gtmTracking';

const activePages = new Map();

function getPageLoadLatency() {
    const performance = window.performance;
    const navigationEntry = performance?.getEntriesByType?.('navigation')?.[0];
    return Math.round(navigationEntry?.loadEventEnd || performance?.now?.() || 0);
}

export default function useGtmTracking(viewName, eventName, eventType = GtmEventType.PageLoad) {
    const pageViewId = useGtmPageViewId(viewName);
    const clientConfiguration = useContext(ClientConfigurationContext);
    const geaoContext = useContext(GeaoContext);
    const isTagManagerEnabled = clientConfiguration?.enableTagManager && clientConfiguration?.tagManagerUrl != null && clientConfiguration?.tagManagerUrl.trim() !== "";

    const interviewIdsRef = useRef();
    interviewIdsRef.current = {
        agentInterviewId: geaoContext?.agentInterview?.interviewId,
        agencyInterviewId: geaoContext?.agencyInterview?.interviewId
    };

    useEffect(() => {
        if (!isTagManagerEnabled || typeof window === 'undefined') {
            return;
        }

        const { pageName, previousPageName } = resolvePageNames(`AgencyIdentity:${viewName}`);

        const trackEvent = () => {
            return trackGtmEvent(eventName, buildGtmEventMetadata({
                pageName,
                pageViewId,
                eventType,
                agentInterviewId: interviewIdsRef.current.agentInterviewId,
                agencyInterviewId: interviewIdsRef.current.agencyInterviewId,
                previousPageName,
                pageLoadLatency: getPageLoadLatency()
            }));
        };

        if (trackEvent()) {
            return;
        }

        console.warn('GTM tracking not available - window._gtm is undefined');

        const checkGtmInterval = setInterval(() => {
            if (trackEvent()) {
                clearInterval(checkGtmInterval);
            }
        }, 100);

        // Stop retrying after 1 second to avoid infinite checking.
        const stopRetryTimeout = setTimeout(() => {
            clearInterval(checkGtmInterval);
        }, 1000);

        return () => {
            clearInterval(checkGtmInterval);
            clearTimeout(stopRetryTimeout);
        };
    }, [eventName, eventType, isTagManagerEnabled, viewName, pageViewId]);

    useEffect(() => {
        if (!isTagManagerEnabled || eventType !== GtmEventType.PageLoad || typeof window === 'undefined') {
            return;
        }

        const pageName = `AgencyIdentity:${viewName}`;
        let page = activePages.get(pageName);
        if (!page) {
            const { previousPageName } = resolvePageNames(pageName);
            page = {
                count: 0,
                startedAt: Date.now(),
                previousPageName,
                pageViewId,
                browserExiting: false
            };
            activePages.set(pageName, page);
            page.handlePageHide = () => { page.browserExiting = true; };
            page.handlePageShow = () => { page.browserExiting = false; };
            window.addEventListener('pagehide', page.handlePageHide);
            window.addEventListener('pageshow', page.handlePageShow);
        }
        page.count += 1;

        return () => {
            page.count -= 1;
            if (page.count > 0) {
                return;
            }

            activePages.delete(pageName);
            window.removeEventListener('pagehide', page.handlePageHide);
            window.removeEventListener('pageshow', page.handlePageShow);
            if (page.browserExiting) {
                return;
            }

            const tracked = trackGtmEvent('PageView', buildGtmEventMetadata({
                pageName,
                pageViewId: page.pageViewId,
                eventType: GtmEventType.PageExit,
                agentInterviewId: interviewIdsRef.current.agentInterviewId,
                agencyInterviewId: interviewIdsRef.current.agencyInterviewId,
                previousPageName: page.previousPageName,
                pageLoadLatency: getPageLoadLatency(),
                timeOnPage: Date.now() - page.startedAt
            }));
            if (!tracked) {
                console.warn('GTM PageExit tracking not available - window._gtm is undefined');
            }
        };
    }, [eventType, isTagManagerEnabled, viewName, pageViewId]);
};
